import { getCookie } from '@shared/utils/cookies';
import { forceLogout } from '@shared/utils/forceLogout';
import { refreshAccessToken } from '@shared/utils/refreshAccessToken';
import createFetchClient, { Middleware } from 'openapi-fetch';
import qs from 'qs';

import createApiClient from '@shared/lib/openapi-tanstack-query';

import type { paths as apiPaths } from './fastfive';

// 인증 실패 재시도를 위해 요청 복제본을 저장 (body 소비 전 clone)
const requestClones = new WeakMap<Request, Request>();

/**
 * 토큰 갱신을 시도할 상태 코드.
 * 백엔드가 만료 토큰에 401·403 을 모두 쓸 수 있어 동일 취급한다.
 * 권한 부족으로 인한 403 은 갱신 후 재시도에서도 403 이 나오지만,
 * 갱신 자체는 성공하므로 강제 로그아웃까지 가지는 않는다.
 */
const AUTH_FAILURE_STATUSES = [401, 403];

const middleware: Middleware = {
  async onRequest({ request }) {
    const token = getCookie('accessToken');
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
    }
    requestClones.set(request, request.clone());
    return request;
  },
  async onResponse({ request, response }) {
    if (AUTH_FAILURE_STATUSES.includes(response.status)) {
      const clone = requestClones.get(request);
      requestClones.delete(request);

      const refreshed = await refreshAccessToken();
      if (refreshed && clone) {
        const newToken = getCookie('accessToken');
        if (newToken) {
          clone.headers.set('Authorization', `Bearer ${newToken}`);
          return fetch(clone);
        }
      }
      await forceLogout();
    }
    requestClones.delete(request);
    return response;
  },
};

const querySerializer = (params: Record<string, unknown>) => {
  return qs.stringify(params, { arrayFormat: 'repeat' });
};

export const fetchClient = createFetchClient<apiPaths>({
  baseUrl: process.env.NEXT_PUBLIC_WEB_API_URI_CLIENT,
  querySerializer,
});

fetchClient.use(middleware);

export const $api = createApiClient(fetchClient);
